/**
 * PhotoGallery — lightbox modal for browsing all listing photos.
 *
 * Renders a full-screen overlay with the active photo, prev/next controls,
 * and a small thumbnail strip. Falls back gracefully if the primary
 * image_url isn't in the photos array (adds it as photo #1).
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <PhotoGallery photos={property.photos} coverImage={property.image_url}
 *                  isOpen={open} onClose={() => setOpen(false)} />
 */
import { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const PhotoGallery = ({
  photos = [],
  coverImage = null,
  address = "Property",
  isOpen,
  onClose,
}) => {
  // Compose the display list: coverImage first (deduped), then rest.
  const allPhotos = useMemo(() => {
    const combined = [];
    if (coverImage && String(coverImage).trim()) combined.push(String(coverImage));
    for (const p of photos || []) {
      if (p && !combined.includes(p)) combined.push(p);
    }
    return combined;
  }, [photos, coverImage]);

  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (isOpen) setIdx(0);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % allPhotos.length);
      if (e.key === "ArrowLeft")
        setIdx((i) => (i - 1 + allPhotos.length) % allPhotos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, allPhotos.length, onClose]);

  if (!isOpen || allPhotos.length === 0) return null;

  const canNav = allPhotos.length > 1;
  const current = allPhotos[idx];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Photo gallery — ${address}`}
      data-testid="photo-gallery-modal"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
        aria-label="Close gallery"
        data-testid="photo-gallery-close"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/10 text-white text-xs font-medium">
        {idx + 1} / {allPhotos.length}
      </div>

      {/* Prev */}
      {canNav && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIdx((i) => (i - 1 + allPhotos.length) % allPhotos.length);
          }}
          className="absolute left-4 md:left-8 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
          aria-label="Previous photo"
          data-testid="photo-gallery-prev"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}

      {/* Main image */}
      <img
        src={current}
        alt={`${address} — photo ${idx + 1}`}
        className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="photo-gallery-image"
      />

      {/* Next */}
      {canNav && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIdx((i) => (i + 1) % allPhotos.length);
          }}
          className="absolute right-4 md:right-8 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
          aria-label="Next photo"
          data-testid="photo-gallery-next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}

      {/* Thumbnail strip */}
      {canNav && (
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 max-w-[90vw] overflow-x-auto px-2 py-1 rounded-lg bg-black/40"
          onClick={(e) => e.stopPropagation()}
        >
          {allPhotos.map((p, i) => (
            <button
              key={p + i}
              type="button"
              onClick={() => setIdx(i)}
              className={`shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition ${
                i === idx ? "border-emerald-400" : "border-transparent opacity-60 hover:opacity-100"
              }`}
              data-testid={`photo-gallery-thumb-${i}`}
              aria-label={`Go to photo ${i + 1}`}
            >
              <img src={p} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoGallery;
